import { Line } from '@react-three/drei';
import { Vector3 } from 'three';
import BoundingBox from './BoundingBox.tsx';
import {
  calculateBoundingBox,
  centerPoints,
  getGcodeUnit,
  parseGCode,
} from './utils.ts';

interface GCodeRendererProps {
  gcode: string;
  modelColor: string;
  boundingBoxColor: string;
  dTextColor: string;
}

function GCodeRenderer({
  gcode,
  modelColor,
  boundingBoxColor,
  dTextColor,
}: GCodeRendererProps) {
  const { rapidMovePoints, linearMovePoints } = parseGCode(gcode);

  // Ensure these are of type Vector3[]
  const allPoints: Vector3[] = [...rapidMovePoints, ...linearMovePoints];

  // Bounding box calculations
  const boundingBox = calculateBoundingBox(allPoints);

  // Center the points based on the bounding box
  const centeredRapidMovePoints = centerPoints(rapidMovePoints, boundingBox);
  const centeredLinearMovePoints = centerPoints(linearMovePoints, boundingBox);

  // Recalculate bounding box for centered points
  const centeredBoundingBox = calculateBoundingBox([
    ...centeredRapidMovePoints,
    ...centeredLinearMovePoints,
  ]);

  const gcodeMeasuringUnit = getGcodeUnit(gcode);

  return (
    <>
      {centeredRapidMovePoints.length > 1 && (
        <Line
          points={centeredRapidMovePoints.map((p) => [p.x, p.y, p.z])} // Convert to [x, y, z] array format
          color="red"
          lineWidth={1}
          opacity={0.5}
          transparent
        />
      )}
      {centeredLinearMovePoints.length > 1 && (
        <Line
          points={centeredLinearMovePoints.map((p) => [p.x, p.y, p.z])} // Convert to [x, y, z] array format
          color={modelColor}
          lineWidth={2}
          opacity={0.6}
          transparent
        />
      )}
      {centeredBoundingBox && (
        <BoundingBox
          box={centeredBoundingBox}
          boundingBoxColor={boundingBoxColor}
          dTextColor={dTextColor}
          unit={gcodeMeasuringUnit}
        />
      )}
    </>
  );
}

export default GCodeRenderer;
